import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-input shadow-sm hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

// D9 (enmienda 2026-10-29): las acciones `btn-*` del design system
// (`src/index.css`) declaran su propia métrica (`text-xs`, `font-semibold`,
// `rounded-sm`) y NO declaran estado de foco; las utilidades base de este
// componente (`text-sm`, `font-medium`, `rounded-md`, `whitespace-nowrap`,
// `focus-visible:ring-1`) las sustituyen en la cascada (capa `utilities` por
// encima de `components`). Esta clase compensa ambas cosas:
//  - métrica de `.btn-primary`/`.btn-secondary` (`text-xs font-semibold
//    rounded-sm`);
//  - anillo de foco que CONTRASTA con el relleno (`ring-2` + separación del
//    fondo con `ring-offset`), porque `--ring` es del mismo color que el
//    relleno de `.btn-primary`;
//  - salto de línea permitido (`!whitespace-normal`) para que el rótulo de la
//    acción primaria siga siendo legible a 320 px de ancho. El modificador
//    `!` (importante) es NECESARIO: la base del componente declara
//    `whitespace-nowrap` y las dos utilidades salen en la MISMA capa de
//    Tailwind (`utilities`), así que sin `!` gana la que se emite después
//    (`whitespace-nowrap`) y el rótulo nunca se parte.
export const actionButtonClass = (btnClass) =>
  [
    'w-full h-auto min-h-10',
    btnClass,
    'text-xs font-semibold rounded-sm',
    '!whitespace-normal leading-tight',
    'focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
  ].join(' ');

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props} />
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
